import { useEffect, useRef, useCallback } from "react";
import { ingestActivityLogs } from "../../services/quizSessionService";

// Helper to ensure question_id is a valid UUID string, or null
const UUID_REGEX = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

const sanitizeQuestionId = (id) => {
  if (!id) return null;
  const strId = String(id).trim();
  return UUID_REGEX.test(strId) ? strId : null;
};

// Modifiers to ignore as standalone keystroke spam
const IGNORE_STANDALONE_KEYS = new Set([
  "Shift",
  "Control",
  "Alt",
  "CapsLock",
  "NumLock",
  "ScrollLock",
]);

const FLUSH_INTERVAL_MS = 30000; // 30 seconds
const MAX_BUFFER_SIZE = 30; // Flush immediately when buffer hits 30 events

/**
 * useSessionTelemetry — Captures and buffers student activity events
 * (keystrokes, copy/paste, restricted shortcuts, and tab switches) during an exam,
 * periodically batching them to POST /quiz-session/<sessionId>/activity-logs/
 *
 * @param {object} params
 * @param {boolean} params.enabled - Whether telemetry is currently active
 * @param {string} params.sessionId - The quiz session UUID
 * @param {string|null} params.currentQuestionId - Currently active question ID (UUID)
 * @returns {{ flushTelemetry: () => Promise<void>, logActivity: (event: object) => void }}
 */
export function useSessionTelemetry({
  enabled = false,
  sessionId,
  currentQuestionId = null,
}) {
  const bufferRef = useRef([]);
  const isFlushingRef = useRef(false);
  const lastTabSwitchTimeRef = useRef(0);
  const activeQuestionIdRef = useRef(currentQuestionId);

  // Keep question ID ref up to date
  useEffect(() => {
    activeQuestionIdRef.current = currentQuestionId;
  }, [currentQuestionId]);

  // Flush buffer to the backend endpoint
  const flushTelemetry = useCallback(async () => {
    if (!sessionId || bufferRef.current.length === 0 || isFlushingRef.current) {
      return;
    }

    isFlushingRef.current = true;
    const eventsToSend = [...bufferRef.current];
    bufferRef.current = [];

    try {
      await ingestActivityLogs(sessionId, eventsToSend);
    } catch (err) {
      console.warn("Telemetry batch upload failed, retrying next cycle:", err);
      // Put failed events back at the front of the buffer to retry
      bufferRef.current = [...eventsToSend, ...bufferRef.current];
    } finally {
      isFlushingRef.current = false;
    }
  }, [sessionId]);

  // Ingest a single event into the buffer
  const logActivity = useCallback(
    ({ event_type, key = null, code = null, metadata = {} }) => {
      if (!enabled || !sessionId) return;

      const event = {
        event_type,
        timestamp: Date.now(),
        question_id: sanitizeQuestionId(activeQuestionIdRef.current),
        metadata,
      };

      if (key) event.key = String(key);
      if (code) event.code = String(code);

      bufferRef.current.push(event);

      // If buffer reached maximum batch size, flush immediately
      if (bufferRef.current.length >= MAX_BUFFER_SIZE) {
        flushTelemetry();
      }
    },
    [enabled, sessionId, flushTelemetry]
  );

  useEffect(() => {
    if (!enabled || !sessionId) return;

    // --- 1. KEYSTROKE & SHORTCUT VIOLATIONS ---
    const handleKeyDown = (e) => {
      const isCtrlOrCmd = e.ctrlKey || e.metaKey;
      const keyLower = (e.key || "").toLowerCase();

      // Detect prohibited shortcuts
      if (isCtrlOrCmd && keyLower === "c") {
        logActivity({
          event_type: "shortcut_violation",
          key: e.key,
          code: e.code,
          metadata: {
            combination: "Ctrl+C",
            description: "Attempted copy shortcut",
          },
        });
        return;
      }

      if (isCtrlOrCmd && keyLower === "v") {
        logActivity({
          event_type: "shortcut_violation",
          key: e.key,
          code: e.code,
          metadata: {
            combination: "Ctrl+V",
            description: "Attempted paste shortcut",
          },
        });
        return;
      }

      if (isCtrlOrCmd && (keyLower === "a" || keyLower === "x")) {
        logActivity({
          event_type: "shortcut_violation",
          key: e.key,
          code: e.code,
          metadata: {
            combination: `Ctrl+${e.key.toUpperCase()}`,
            description: `Attempted ${keyLower === "a" ? "select all" : "cut"} shortcut`,
          },
        });
        return;
      }

      if (e.key === "F12" || (isCtrlOrCmd && e.shiftKey && (keyLower === "i" || keyLower === "j"))) {
        logActivity({
          event_type: "shortcut_violation",
          key: e.key,
          code: e.code,
          metadata: {
            combination: e.key,
            description: "Attempted developer tools shortcut",
          },
        });
        return;
      }

      if (e.altKey && e.key === "Tab") {
        logActivity({
          event_type: "shortcut_violation",
          key: e.key,
          code: e.code,
          metadata: {
            combination: "Alt+Tab",
            description: "Attempted window switch shortcut",
          },
        });
        return;
      }

      if (e.key === "Meta") {
        logActivity({
          event_type: "shortcut_violation",
          key: e.key,
          code: e.code,
          metadata: {
            combination: "Meta",
            description: "Attempted Windows / Command key",
          },
        });
        return;
      }

      if (e.key === "PrintScreen") {
        logActivity({
          event_type: "shortcut_violation",
          key: e.key,
          code: e.code,
          metadata: {
            combination: "PrintScreen",
            description: "Attempted screenshot shortcut",
          },
        });
        return;
      }

      // Ignore standalone modifier keys alone
      if (IGNORE_STANDALONE_KEYS.has(e.key)) return;

      // Normal Keystroke
      logActivity({
        event_type: "keystroke",
        key: e.key,
        code: e.code,
        metadata: {
          combination: e.key,
        },
      });
    };

    // --- 2. COPY & PASTE LISTENERS ---
    const handlePaste = (e) => {
      const pastedText = e.clipboardData?.getData("text") || "";
      logActivity({
        event_type: "paste",
        metadata: {
          pasted_length: pastedText.length,
          preview: pastedText.slice(0, 50),
          combination: "Ctrl+V",
        },
      });
    };

    const handleCopy = () => {
      const selectedText = window.getSelection()?.toString() || "";
      logActivity({
        event_type: "copy",
        metadata: {
          copied_length: selectedText.length,
          combination: "Ctrl+C",
        },
      });
    };

    // --- 3. TAB SWITCH / VISIBILITY LOSS ---
    const registerTabSwitch = (type) => {
      const now = Date.now();
      // Debounce tab switches by 500ms to avoid blur + hidden duplicates
      if (now - lastTabSwitchTimeRef.current < 500) return;
      lastTabSwitchTimeRef.current = now;

      logActivity({
        event_type: "tab_switch",
        metadata: {
          type,
        },
      });
    };

    const handleVisibilityChange = () => {
      if (document.hidden) {
        registerTabSwitch("visibility_hidden");
      }
    };

    const handleBlur = () => {
      registerTabSwitch("blur");
    };

    // Attach listeners
    window.addEventListener("keydown", handleKeyDown, true);
    document.addEventListener("paste", handlePaste, true);
    document.addEventListener("copy", handleCopy, true);
    document.addEventListener("visibilitychange", handleVisibilityChange);
    window.addEventListener("blur", handleBlur);

    // Periodic flush timer (every 30s)
    const intervalId = setInterval(() => {
      flushTelemetry();
    }, FLUSH_INTERVAL_MS);

    // Flush on page unload / leave
    const handleBeforeUnload = () => {
      flushTelemetry();
    };
    window.addEventListener("beforeunload", handleBeforeUnload);

    return () => {
      window.removeEventListener("keydown", handleKeyDown, true);
      document.removeEventListener("paste", handlePaste, true);
      document.removeEventListener("copy", handleCopy, true);
      document.removeEventListener("visibilitychange", handleVisibilityChange);
      window.removeEventListener("blur", handleBlur);
      window.removeEventListener("beforeunload", handleBeforeUnload);
      clearInterval(intervalId);

      // Perform final flush on unmount
      flushTelemetry();
    };
  }, [enabled, sessionId, logActivity, flushTelemetry]);

  return {
    flushTelemetry,
    logActivity,
  };
}

export default useSessionTelemetry;
